import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { useUploadSnapshot } from '@/dataAccess/rest';

/**
 * Remote persistence state. Every capture — live or DVR — uploads automatically, so the
 * normal path is 'saving' → 'saved'. 'local' means the capture exists in this session
 * ONLY: the storage API call failed (or a caller opted out of auto-persist). It is
 * viewable and downloadable until reload, and the operator can retry the upload from the
 * DVR viewer.
 */
export type SnapshotPersistState = 'saving' | 'saved' | 'local';

export type VideoSnapshot = {
  id: string;
  /** Object URL for immediate in-browser display; revoked on removal/unmount. */
  url: string;
  blob: Blob;
  /** Wall-clock moment the captured FRAME represents (capture time minus DVR offset). */
  frameAt: Date;
  /** Seconds behind live when captured (0 = at the live edge, i.e. every live capture). */
  offsetFromLiveSeconds: number;
  /**
   * Storage identity captured AT CAPTURE TIME. The widget is not remounted when the
   * operator switches camera or stream tier, so a later on-demand upload must use these
   * — not whatever is selected now — or the image lands under the wrong combo.
   */
  cameraId: string;
  tier: string;
  /** On-cluster persistence (SCO-2405); 'local' when the storage call failed. */
  persistState: SnapshotPersistState;
  /** Storage-relative path on the cluster once persisted. */
  remotePath?: string;
};

/** Session cap for the LIVE strip; history beyond this lives on-cluster (SCO-2405). */
const MAX_SNAPSHOTS = 12;
const JPEG_QUALITY = 0.92;

type PersistSnapshotFn = (args: {
  blob: Blob;
  frameAt: Date;
  cameraId: string;
  tier: string;
}) => Promise<{ path: string } | null>;

type UseVideoSnapshotsOptions = {
  /**
   * Storage identity of the stream being viewed, stamped onto each capture. Passed as
   * scalars rather than an object so callers need no `useMemo` to keep the hook stable.
   * The tier must describe the FRAME (rewind always recalls HIGH), not the live selection.
   */
  cameraId: string;
  tier: string;
};

export type CaptureSnapshotOptions = {
  /** 0 for live captures; the DVR playhead offset when rewinding. */
  offsetFromLiveSeconds?: number;
  /**
   * Upload immediately after capture. Defaults to true and both call sites rely on that:
   * cluster storage is the default for live and DVR alike. Opting out leaves the capture
   * session-only until {@link UseVideoSnapshots.persistSnapshot} is called for it.
   */
  autoPersist?: boolean;
};

type UseVideoSnapshots = {
  snapshots: VideoSnapshot[];
  /**
   * Grab the current frame of `videoRef`. No-op while the video has no decoded frame, and
   * no-op for a repeat press inside the same second for the same camera+tier (that second
   * is one stored file, so it must be one strip entry).
   */
  captureSnapshot: (options?: CaptureSnapshotOptions) => Promise<void>;
  /** Retry the upload for a snapshot that is session-only ('local'). */
  persistSnapshot: (id: string) => void;
  removeSnapshot: (id: string) => void;
};

/**
 * Snapshot store for the camera stream `<video>` element (SCO-2290 capture,
 * SCO-2405 persistence). Frames are captured via canvas into JPEG blobs. Both players
 * feed this one store and both persist to the cluster in the background — a live
 * capture is silent, with no preview over the live feed.
 *
 * Captures surface in the DVR filmstrip (`CameraSnapshotsOverlay`), which is where the
 * operator downloads, deletes, or retries a failed upload. The in-memory object URLs are
 * session-only and revoked on unmount/reload — the stored copies survive on the cluster.
 */
export const useVideoSnapshots = (
  videoRef: RefObject<HTMLVideoElement | null>,
  { cameraId, tier }: UseVideoSnapshotsOptions
): UseVideoSnapshots => {
  const [snapshots, setSnapshots] = useState<VideoSnapshot[]>([]);

  // Mirror for unmount cleanup without re-running the effect per capture.
  const snapshotsRef = useRef<VideoSnapshot[]>([]);
  snapshotsRef.current = snapshots;

  useEffect(
    () => () => {
      for (const snapshot of snapshotsRef.current) URL.revokeObjectURL(snapshot.url);
    },
    []
  );

  const markPersisted = useCallback((id: string, remotePath: string | null) => {
    setSnapshots(previous =>
      previous.map(snapshot =>
        snapshot.id === id
          ? {
              ...snapshot,
              persistState: remotePath ? 'saved' : 'local',
              remotePath: remotePath ?? undefined,
            }
          : snapshot
      )
    );
  }, []);

  const { mutateAsync: uploadSnapshot } = useUploadSnapshot();

  /**
   * Upload one capture to cluster storage. `cameraId`/`tier` come from the SNAPSHOT
   * (stamped at capture), never from the hook's current options — the widget is not
   * remounted when the operator switches camera or tier, so an on-demand upload after a
   * switch must still land under the combo the frame was taken from. Resolving null
   * marks it 'local': viewable and downloadable in-session, just not on the cluster.
   */
  const persist = useCallback<PersistSnapshotFn>(
    async ({ blob, frameAt, cameraId: capturedCameraId, tier: capturedTier }) => {
      if (!capturedCameraId || !capturedTier) return null;
      const remote = await uploadSnapshot({
        blob,
        cameraId: capturedCameraId,
        tier: capturedTier,
        frameAt,
      });
      return { path: remote.path };
    },
    [uploadSnapshot]
  );

  const runPersist = useCallback(
    (id: string, snapshot: Pick<VideoSnapshot, 'blob' | 'frameAt' | 'cameraId' | 'tier'>) => {
      const { blob, frameAt, cameraId: capturedCameraId, tier: capturedTier } = snapshot;
      persist({ blob, frameAt, cameraId: capturedCameraId, tier: capturedTier })
        .then(remote => markPersisted(id, remote?.path ?? null))
        .catch(error => {
          console.warn('Snapshot persist failed:', error);
          markPersisted(id, null);
        });
    },
    [persist, markPersisted]
  );

  const captureSnapshot = useCallback(
    async ({ offsetFromLiveSeconds = 0, autoPersist = true }: CaptureSnapshotOptions = {}) => {
      const video = videoRef.current;
      // videoWidth 0 ⇒ no decoded frame yet (e.g. rewind still buffering); nothing to grab.
      if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;

      const offset = Math.max(0, Math.round(offsetFromLiveSeconds));
      const frameAt = new Date(Date.now() - offset * 1000);

      // Storage identity is `{cameraId}_{tier}` + a SECOND-precision timestamp, and the
      // server keeps the first write for a given second. Mirror that here so the strip
      // can't show several entries that all map to the one stored file: repeated presses
      // inside the same second (faster than the recall stream advances) are no-ops.
      // Skipping before the canvas/JPEG work also avoids the wasted encode.
      const isDuplicateSecond = snapshotsRef.current.some(
        existing =>
          existing.cameraId === cameraId &&
          existing.tier === tier &&
          Math.floor(existing.frameAt.getTime() / 1000) === Math.floor(frameAt.getTime() / 1000)
      );
      if (isDuplicateSecond) return;

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.drawImage(video, 0, 0);

      const blob = await new Promise<Blob | null>(resolve => {
        try {
          canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
        } catch (error) {
          // SecurityError ⇒ tainted canvas (native-HLS segments without CORS headers).
          console.warn('Snapshot capture failed:', error);
          resolve(null);
        }
      });
      if (!blob) return;

      const snapshot: VideoSnapshot = {
        id: uuidv4(),
        url: URL.createObjectURL(blob),
        blob,
        // Live captures are at the live edge, so this is simply "now" — the same
        // second-precision wall clock the DVR path derives from its playhead offset.
        frameAt,
        offsetFromLiveSeconds: offset,
        cameraId,
        tier,
        persistState: autoPersist ? 'saving' : 'local',
      };

      setSnapshots(previous => {
        const next = [snapshot, ...previous];
        for (const dropped of next.slice(MAX_SNAPSHOTS)) URL.revokeObjectURL(dropped.url);
        return next.slice(0, MAX_SNAPSHOTS);
      });

      // Background upload; local strip never waits on the network.
      if (autoPersist) runPersist(snapshot.id, snapshot);
    },
    [videoRef, cameraId, tier, runPersist]
  );

  const persistSnapshot = useCallback(
    (id: string) => {
      const snapshot = snapshotsRef.current.find(candidate => candidate.id === id);
      if (!snapshot || snapshot.persistState === 'saving' || snapshot.persistState === 'saved') {
        return;
      }
      setSnapshots(previous =>
        previous.map(candidate =>
          candidate.id === id ? { ...candidate, persistState: 'saving' } : candidate
        )
      );
      // Uses the identity stamped at capture, so uploading after a camera/tier switch
      // still stores the image under the combo it was actually taken from.
      runPersist(id, snapshot);
    },
    [runPersist]
  );

  const removeSnapshot = useCallback((id: string) => {
    setSnapshots(previous => {
      const removed = previous.find(snapshot => snapshot.id === id);
      if (removed) URL.revokeObjectURL(removed.url);
      return previous.filter(snapshot => snapshot.id !== id);
    });
  }, []);

  return { snapshots, captureSnapshot, persistSnapshot, removeSnapshot };
};
